import { useUserIdentityContext } from "@core/services/user-identity.context";
import TagColor from "@core/ui/tag-color/tag-color";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetCouponModel } from "@factory/model/query-model-coupon";
import { useQyGetCoupon } from "@factory/query/coupon.query";
import ErrorSection from "@shared/ui/error-section/error-section";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import TextBlock from "@shared/ui/text-block/text-block";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { DataView } from "primereact/dataview";
import { PaginatorPageChangeEvent } from "primereact/paginator";
import { useState } from "react";

import { appFeature } from "../../../app.feature";

interface CustomerListCouponsProps {
  action: string;
}

export function CustomerListCoupons({ action }: CustomerListCouponsProps) {
  // THIS IS THE LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getAuthUser } = useUserIdentityContext();
  const { userCode } = getAuthUser();
  const { pointsText, stampsText } = appFeature.config.label;
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [sort] = useState({});
  const [layout] = useState<
    "grid" | "list" | (string & Record<string, unknown>)
  >("grid");

  // THIS IS THE QUERY DECLARATION
  const {
    data: couponResponse,
    isLoading,
    isError,
    error,
  } = useQyGetCoupon(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    {
      ...(action === "REDEEM" && { userCode }),
    },
    true,
  );

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const gridItem = (coupon: GetCouponModel) => {
    return (
      <div
        className="flex justify-between shadow-md rounded-md overflow-hidden border"
        key={coupon.code}
      >
        <div
          className={`flex items-center justify-center w-20 ${coupon.purpose === "REDEEM" ? "bg-orange-200 text-orange-700" : "bg-green-200 text-green-700"}`}
        >
          <span className={`transform -rotate-90 whitespace-nowrap text-lg`}>
            {coupon.purpose}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-5">
          <div className="mb-2">
            <p className="text-gray-500 font-semibold">{coupon.name}</p>
            <p className="text-2xl font-semibold py-2">{coupon.coupon_code}</p>
            <p className="text-xs text-gray-400 font-semibold">
              Valid until: {formatDate(coupon.valid_until)}
            </p>
            {coupon?.redeem_limit !== undefined && coupon?.redeem_limit > 0 && (
              <p className="text-sm text-gray-400 font-semibold">
                {coupon.claimed_count}/{coupon.redeem_limit?.toString()}{" "}
                <span>Claimed</span>
              </p>
            )}
          </div>
          <div className="flex justify-between">
            <div>
              {coupon.points && coupon.points > 0 ? (
                <>
                  <p className="text-2xl font-bold">
                    {coupon.points?.toString()}
                  </p>
                  <span>Points</span>
                </>
              ) : null}
            </div>
            <div>
              {coupon.stamps && coupon.stamps > 0 ? (
                <>
                  <p className="text-2xl font-bold">
                    {coupon.stamps?.toString()}
                  </p>
                  <span>Stamps</span>
                </>
              ) : null}
            </div>
          </div>
        </div>
        <div>
          <div className="flex flex-col justify-end items-end p-5">
            <TagColor
              value={
                coupon.is_claimed_by_user
                  ? "CLAIMED"
                  : coupon.claimed_count === coupon.redeem_limit
                    ? "NOT_AVAILABLE"
                    : "AVAILABLE"
              }
            />
          </div>
        </div>
      </div>
    );
  };
  const listItem = (coupon: GetCouponModel, index: number) => {
    return (
      <div
        className="flex justify-between shadow-md rounded-md overflow-hidden border p-5"
        key={coupon.code}
      >
        <div className="flex flex-col">
          <TextBlock label="Coupon Name" value={coupon.name} />
          <div>
            <TextBlock
              label={`${pointsText} ${coupon.purpose === "REDEMPTION" ? "Redeemed" : "to Earn"}`}
              value={`${coupon.points?.toString()}`}
            />
            <TextBlock
              label={`${stampsText} ${coupon.purpose === "REDEMPTION" ? "Redeemed" : "to Earn"}`}
              value={`${coupon.stamps?.toString()}`}
            />
            {coupon?.redeem_limit !== undefined && coupon?.redeem_limit > 0 && (
              <TextBlock
                label={`Claimed`}
                value={`${coupon.claimed_count}/${coupon.redeem_limit?.toString()}`}
              />
            )}
          </div>
        </div>
        <div>
          <div className="flex flex-col justify-end items-end">
            <TagColor
              value={
                (coupon.claimed_count || 1) < (coupon.redeem_limit || 1)
                  ? !coupon.is_claimed_by_user
                    ? "AVAILABLE"
                    : "CLAIMED"
                  : "NOT_AVAILABLE"
              }
            />
            <TextBlock label="Coupon Code" value={coupon.coupon_code} />
          </div>
        </div>
      </div>
    );
  };

  const itemTemplate = (
    coupon: GetCouponModel,
    layout: "grid" | "list",
    index: number,
  ) => {
    if (!coupon) {
      return;
    }
    if (layout === "list") return listItem(coupon, index);
    else if (layout === "grid") return gridItem(coupon);
  };
  const listTemplate = (
    coupons: GetCouponModel[],
    layout: "grid" | "list",
  ): any => {
    return (
      <div
        className={`grid ${layout === "grid" ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
          } gap-4 mt-4`}
      >
        {coupons.map((coupon, index) => itemTemplate(coupon, layout, index))}
      </div>
    );
  };

  const grid = (
    <section>
      <h5 className="mb-2">
        Total: <b>{couponResponse?.count}</b>
      </h5>
      <DataView
        value={couponResponse?.data}
        listTemplate={listTemplate}
        layout={layout}
        paginator
        rows={rowLimit}
        first={first}
        totalRecords={couponResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPage={onPageChange}
      />
    </section>
  );

  return (
    <div id="ListActivities" className="campaign">
      <div className="p-4 bg-white">
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default CustomerListCoupons;
